<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="rachunki-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-receipt-text</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">Rachunki</h1>
      </div>
    </div>
    <v-card>
      <v-card-title>
        <v-icon left>mdi-receipt-text</v-icon>
        {{ $t('receipts.title') }}
      </v-card-title>
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedMonth"
                :items="months"
                item-text="label"
                item-value="value"
                :label="$t('receipts.month')"
                outlined
                dense
                return-object
                @change="loadData"
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="subCompanyId"
                :items="subCompanyOptions"
                item-text="name"
                item-value="id"
                label="Oddział"
                outlined
                dense
                @change="loadData"
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                :label="$t('receipts.searchDriver')"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-magnify"
                @input="debouncedSearch"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="text-right">
              <v-btn
                color="primary"
                :loading="downloadingZip"
                :disabled="!totalCount"
                @click="downloadZip"
              >
                <v-icon left>mdi-folder-zip</v-icon>
                {{ $t('receipts.downloadZip') }} ({{ totalCount }})
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            :server-items-length="totalCount"
            :options.sync="tableOptions"
            @update:options="onOptionsChange"
            class="elevation-1"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          >
            <template v-slot:item.driverName="{ item }">
              <a
                href="#"
                @click.prevent="goToDriver(item.driverId)"
                class="text-decoration-none"
              >
                {{ item.lastName }} {{ item.firstName }}
              </a>
            </template>

            <template v-slot:item.cooperationType="{ item }">
              {{ cooperationTypeLabel(item.cooperationType) }}
            </template>

            <template v-slot:item.taxRyczaltZl="{ item }">
              <!-- Backend liczy ryczałt w PEŁNYCH ZŁOTYCH (tak jak apka kierowcy),
                   więc nie przepuszczamy tego przez formatPLN, który dzieli grosze. -->
              {{ item.taxRyczaltZl != null ? item.taxRyczaltZl + " zł" : "—" }}
            </template>
            <template v-slot:item.totalRent="{ item }">
              {{ formatPLN(item.totalRent) }}
            </template>

            <template v-slot:item.invoiceCount="{ item }">
              {{ item.invoiceCount || 0 }}
            </template>

            <template v-slot:item.invoiceTotalBrutto="{ item }">
              {{ formatPLN(item.invoiceTotalBrutto || 0) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                color="red"
                :loading="item.downloading"
                @click="downloadPdf(item)"
                :title="$t('receipts.downloadPdf')"
              >
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

const MONTH_NAMES = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

const COOPERATION_LABELS = {
  B2B_VAT: "B2B VAT",
  B2B_NO_VAT: "B2B bez VAT",
  FIFTY_FIFTY: "50/50",
  RENT: "Najem",
  CONNECTION: "Podpięcie",
  STUDENT: "Student",
};

function generateMonths() {
  const months = [];
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // From previous month back to Jan 2024
  let m = currentMonth - 1;
  let y = currentYear;
  if (m < 0) {
    m = 11;
    y--;
  }

  while (y > 2024 || (y === 2024 && m >= 0)) {
    months.push({
      label: `${MONTH_NAMES[m]} ${y}`,
      value: { month: m + 1, year: y },
    });
    m--;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (y < 2024) break;
  }

  return months;
}

export default {
  data() {
    const months = generateMonths();
    return {
      months,
      selectedMonth: months.length > 0 ? months[0] : null,
      search: "",
      subCompanyId: "ALL",
      subCompanyOptions: [{ id: "ALL", name: "Wszystkie" }],
      items: [],
      totalCount: 0,
      loading: false,
      downloadingZip: false,
      searchTimeout: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
      },
      headers: [
        { text: "Kierowca", value: "driverName", sortable: true },
        { text: "Typ współpracy", value: "cooperationType", sortable: true },
        { text: "Suma najmu", value: "totalRent", sortable: true, align: "end" },
        { text: "Liczba faktur", value: "invoiceCount", sortable: true, align: "center" },
        { text: "Wartość faktur (brutto)", value: "invoiceTotalBrutto", sortable: true, align: "end" },
        { text: "Ryczałt 8,5%", value: "taxRyczaltZl", sortable: true, align: "end" },
        { text: "Akcje", value: "actions", sortable: false, align: "center", width: 80 },
      ],
    };
  },
  async created() {
    await this.fetchSubCompanies();
    await this.loadData();
  },
  methods: {
    async fetchSubCompanies() {
      try {
        const r = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cash-report/sub-companies",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const subs = (r.data && r.data.data) || [];
        this.subCompanyOptions = [{ id: "ALL", name: "Wszystkie" }, ...subs];
      } catch (e) {
        this.subCompanyOptions = [{ id: "ALL", name: "Wszystkie" }];
      }
    },
    cooperationTypeLabel(type) {
      return COOPERATION_LABELS[type] || type || "-";
    },
    formatPLN(cents) {
      return (cents / 100).toFixed(2).replace(".", ",") + " zł";
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.tableOptions.page = 1;
        this.loadData();
      }, 400);
    },
    onOptionsChange() {
      this.loadData();
    },
    async toggleLoading(type) {
      localStorage.setItem("loading", type);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: "false",
          },
        })
      );
    },
    async loadData() {
      if (!this.selectedMonth) return;
      this.loading = true;
      await this.toggleLoading("true");

      const { month, year } = this.selectedMonth.value;
      const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
      const skip = (page - 1) * itemsPerPage;
      const sortByVal = Array.isArray(sortBy) ? sortBy[0] : sortBy;
      const sortDescVal = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc;

      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "receipts",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            month,
            year,
            search: this.search || undefined,
            subCompanyId: this.subCompanyId && this.subCompanyId !== "ALL" ? this.subCompanyId : undefined,
            take: itemsPerPage,
            skip,
            sortBy: sortByVal || undefined,
            sortDesc: sortDescVal ? "true" : undefined,
          },
        });

        const data = response.data.data;
        this.items = data.result.map((d) => ({
          ...d,
          downloading: false,
        }));
        this.totalCount = data.count;
      } catch (e) {
        console.error(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Błąd ładowania rachunków", "error");
      }

      this.loading = false;
      await this.toggleLoading("false");
    },
    async downloadPdf(item) {
      item.downloading = true;
      const { month, year } = this.selectedMonth.value;

      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `receipts/pdf/${item.driverId}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: { month, year },
          responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rachunek_${year}_${String(month).padStart(2, "0")}_${item.lastName}_${item.firstName}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
        Swal.fire("Błąd", "Nie udało się pobrać PDF", "error");
      }

      item.downloading = false;
    },
    async downloadZip() {
      this.downloadingZip = true;
      const { month, year } = this.selectedMonth.value;

      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "receipts/zip",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            month,
            year,
            subCompanyId: this.subCompanyId && this.subCompanyId !== "ALL" ? this.subCompanyId : undefined,
          },
          responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rachunki_${year}_${String(month).padStart(2, "0")}.zip`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
        Swal.fire("Błąd", "Nie udało się pobrać ZIP", "error");
      }

      this.downloadingZip = false;
    },
    goToDriver(driverId) {
      this.$router.push({ path: "/driver-details", query: { id: driverId } });
    },
  },
};
</script>

<style scoped>
.rachunki-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }

.rachunki-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.rachunki-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.rachunki-page >>> .v-card .v-card__title .v-icon { color: #5478FF !important; }
.rachunki-page >>> .v-text-field--outlined fieldset, .rachunki-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.rachunki-page >>> .v-text-field--outlined .v-input__slot, .rachunki-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.rachunki-page >>> .v-label { color: #6E7691 !important; }
.rachunki-page >>> .v-text-field input, .rachunki-page >>> .v-select__selection { color: #fff !important; }
.rachunki-page >>> .v-data-table { background: transparent !important; color: #fff !important; }
.rachunki-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.rachunki-page >>> .v-data-table tbody tr { background: transparent !important; }
.rachunki-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.rachunki-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.rachunki-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.rachunki-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.rachunki-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.rachunki-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.rachunki-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
</style>
